import React from 'react';
import { Button as MuiButton } from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

type MealPlanDraftHeaderProps = {
  onBack: () => void;
  clientDisplayName: string;
  clientId?: string | number | null;
  targetCalories?: number | null;
  onSaveChanges?: () => void;
  saving?: boolean;
  saveDisabled?: boolean;
  showSaveButton?: boolean;
  saveButtonLabel?: string;
  activeStep?: 1 | 2 | 3 | 4;
};

const STEP_LABELS = ['Setup', 'Generate', 'Select and Review', 'Approve and Assign'] as const;

const MealPlanDraftHeader: React.FC<MealPlanDraftHeaderProps> = ({
  onBack,
  clientDisplayName,
  clientId,
  targetCalories,
  onSaveChanges,
  saving = false,
  saveDisabled = false,
  showSaveButton = true,
  saveButtonLabel = 'Save Changes',
  activeStep = 3,
}) => {
  const activeIndex = Math.min(4, Math.max(1, activeStep)) - 1;

  return (
    <div className="border-b border-slate-200">
      <div className="border-b border-slate-200 px-4 py-3 sm:px-6 lg:px-7">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-fit gap-1 px-0 text-[14px] font-medium text-slate-600 hover:bg-transparent hover:text-slate-800"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
            <span className="hidden h-6 w-px bg-slate-300 md:block" />
            <div className="md:pl-2">
              <h1 className="text-[15px] font-semibold text-slate-900">AI Meal Plan Generator</h1>
              <p className="mt-1 text-xs text-slate-500">
                Create personalized meal plans for{' '}
                <ClientNameLink clientId={clientId} className="text-slate-600 hover:text-slate-800">
                  {clientDisplayName}
                </ClientNameLink>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {typeof targetCalories === 'number' && targetCalories > 0 ? (
              <div className="inline-flex h-10 items-center gap-2 rounded-xl bg-cyan-100 px-3 text-xs font-semibold text-cyan-700">
                <BarChart3 className="h-4 w-4" />
                EER {Math.round(targetCalories)} kcal
              </div>
            ) : null}
            {showSaveButton ? (
              <MuiButton
                type="button"
                variant="contained"
                startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={onSaveChanges}
                disabled={saving || !onSaveChanges || saveDisabled}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  height: '36px',
                  px: '11px',
                  borderRadius: '8px',
                  bgcolor: '#20AEDC',
                  '&:hover': { bgcolor: '#20AEDC' },
                }}
              >
                {saving ? 'Saving...' : saveButtonLabel}
              </MuiButton>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 sm:px-6 lg:px-7">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 w-full">
          {STEP_LABELS.map((step, index) => (
            <React.Fragment key={step}>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                    index <= activeIndex ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {index + 1}
                </span>
                <span
                  className={`text-[15px] leading-tight ${
                    index <= activeIndex ? 'font-medium text-slate-800' : 'text-slate-400'
                  }`}
                >
                  {step}
                </span>
              </div>
              {index < STEP_LABELS.length - 1 && (
                <div
                  className={`hidden h-px flex-1 md:block ${
                    index < activeIndex ? 'bg-cyan-500' : 'bg-slate-300'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MealPlanDraftHeader;
